import { Component, ElementRef, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowLeft, faCrosshairs, faFileContract, faHouse, faMagnifyingGlass, faUser, faUsers
} from '@fortawesome/pro-light-svg-icons';
import {
  GraphAnchor, GraphKind, GraphNode, OwnerGraph, OwnerGraphService, graphKey
} from '../../../services/owner-graph.service';
import {
  GLOBAL_SEARCH_MIN_LENGTH, GlobalSearchHit, GlobalSearchService
} from '../../../services/global-search.service';
import { recordLink } from '../../utils/current-record';
import { PaletteSwitch } from '../command-palette/palette-switch';
import { PaletteSwitcherComponent, PaletteTab } from '../command-palette/palette-switcher.component';
import {
  CARD_H, CARD_W, Direction, GraphLayout, GraphView, LayoutCard,
  hasDistinctFullView, layoutGraph, neighbor
} from './owner-graph-layout';

export interface OwnerGraphPaletteData {
  /** La fiche ouverte à l'appel du raccourci ; `null` hors fiche — la palette s'ouvre alors sur sa recherche. */
  anchor: GraphAnchor | null;
}

const KIND_ICON: Record<GraphKind, IconDefinition> = {
  owner: faUser,
  property: faHouse,
  lease: faFileContract,
  tenant: faUsers,
};

const KIND_NAME: Record<GraphKind, string> = {
  owner: 'Propriétaire',
  property: 'Bien',
  lease: 'Bail',
  tenant: 'Locataire',
};

const DEBOUNCE_MS = 180;

/** 'AAAA-MM-JJ' → 'MM/AAAA', sans passer par `Date` (lu en UTC, il décalerait d'un jour). */
function monthYear(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m] = iso.split('-');
  return y && m ? `${m}/${y}` : null;
}

/**
 * La palette du graphe (Cmd+Shift+K) : où se trouve la fiche ouverte dans le
 * graphe de son propriétaire — biens, baux, locataires —, en colonnes reliées.
 *
 * Deux vues sur un même graphe chargé une fois : « Autour de la fiche » (la
 * fiche et ses voisins) et « Tout le propriétaire ». Les baux terminés sont
 * repliés sous « n baux terminés », dépliables bien par bien.
 *
 * Clavier : flèches entre les cartes (←/→ suivent les liens), Entrée ouvre la
 * fiche, Cmd/Ctrl+Entrée dans un onglet, Espace recentre le graphe sur la
 * carte, Retour arrière revient au centre précédent, T bascule la vue, / ouvre
 * la recherche. Clic = ouvrir la fiche (et fermer), comme Cmd+K.
 */
@Component({
  selector: 'app-owner-graph-palette',
  standalone: true,
  imports: [FontAwesomeModule, RouterLink, PaletteSwitcherComponent],
  templateUrl: './owner-graph-palette.component.html',
})
export class OwnerGraphPaletteComponent implements OnInit, OnDestroy {
  private readonly graphService = inject(OwnerGraphService);
  private readonly searchService = inject(GlobalSearchService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialogRef<OwnerGraphPaletteComponent>);
  private readonly data = inject<OwnerGraphPaletteData>(MAT_DIALOG_DATA);

  private readonly canvas = viewChild<ElementRef<HTMLElement>>('canvas');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly icons = { faArrowLeft, faCrosshairs, faMagnifyingGlass };
  readonly cardW = CARD_W;
  readonly cardH = CARD_H;
  readonly minLength = GLOBAL_SEARCH_MIN_LENGTH;
  readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  readonly anchor = signal<GraphAnchor | null>(null);
  readonly graph = signal<OwnerGraph | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly view = signal<GraphView>('local');
  readonly expanded = signal<ReadonlySet<string>>(new Set());
  readonly focusKey = signal<string | null>(null);
  /** Les centres précédents, pour Retour arrière. */
  readonly history = signal<GraphAnchor[]>([]);

  // --- Recherche (hors fiche, ou « / ») ---
  readonly searching = signal(false);
  readonly query = signal('');
  readonly hits = signal<GlobalSearchHit[]>([]);
  readonly searchActive = signal(0);
  readonly searchLoading = signal(false);
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private searchSeq = 0;
  private graphSeq = 0;

  readonly layout = computed<GraphLayout | null>(() => {
    const graph = this.graph();
    return graph ? layoutGraph(graph, this.view(), this.expanded()) : null;
  });

  readonly canToggleView = computed(() => {
    const graph = this.graph();
    return !!graph && hasDistinctFullView(graph);
  });

  readonly anchorNode = computed(() => {
    const graph = this.graph();
    return graph ? graph.nodes.get(graphKey(graph.anchor.kind, graph.anchor.id)) ?? null : null;
  });

  /** Les arêtes qui touchent la carte active, dessinées par-dessus les autres. */
  readonly edgeClasses = computed(() => {
    const focus = this.focusKey();
    const layout = this.layout();
    return (layout?.edges ?? []).map(e => ({ ...e, active: e.from === focus || e.to === focus }));
  });

  ngOnInit(): void {
    if (this.data?.anchor) {
      void this.setAnchor(this.data.anchor, false);
    } else {
      this.openSearch();
    }
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  // --- Chargement ---

  async setAnchor(anchor: GraphAnchor, remember = true): Promise<void> {
    const previous = this.anchor();
    if (remember && previous) this.history.update(h => [...h, previous]);
    this.anchor.set(anchor);
    this.searching.set(false);
    this.expanded.set(new Set());
    this.loading.set(true);
    this.error.set(false);

    // Même garde que Cmd+K : une réponse lente d'un centre précédent ne doit
    // pas écraser celle du centre courant.
    const seq = ++this.graphSeq;
    try {
      const graph = await this.graphService.getOwnerGraph(anchor);
      if (seq !== this.graphSeq) return;
      this.graph.set(graph);
      const anchorKey = graphKey(anchor.kind, anchor.id);
      const layout = this.layout();
      this.setFocus(layout?.cards.some(c => c.key === anchorKey) ? anchorKey : layout?.cards[0]?.key ?? null, true);
    } catch {
      if (seq !== this.graphSeq) return;
      this.graph.set(null);
      this.error.set(true);
    }
    this.loading.set(false);
    this.focusCanvas();
  }

  back(): void {
    const stack = this.history();
    if (!stack.length) return;
    this.history.set(stack.slice(0, -1));
    void this.setAnchor(stack[stack.length - 1], false);
  }

  // --- Vue ---

  setView(view: GraphView): void {
    if (!this.canToggleView() && view === 'full') return;
    this.view.set(view);
    this.keepFocusVisible();
  }

  toggleView(): void {
    this.setView(this.view() === 'local' ? 'full' : 'local');
  }

  toggleFold(propertyKey: string): void {
    this.expanded.update(set => {
      const next = new Set(set);
      if (next.has(propertyKey)) next.delete(propertyKey);
      else next.add(propertyKey);
      return next;
    });
    this.keepFocusVisible();
  }

  /** Après un changement de vue, le curseur doit rester sur une carte affichée. */
  private keepFocusVisible(): void {
    const layout = this.layout();
    if (!layout) return;
    const focus = this.focusKey();
    if (focus && layout.cards.some(c => c.key === focus)) return;
    const anchor = this.anchor();
    const anchorKey = anchor ? graphKey(anchor.kind, anchor.id) : null;
    this.setFocus(layout.cards.some(c => c.key === anchorKey) ? anchorKey : layout.cards[0]?.key ?? null);
  }

  // --- Clavier et souris sur le graphe ---

  onCanvasKeydown(event: KeyboardEvent): void {
    if (event.metaKey && event.key !== 'Enter') return;
    const layout = this.layout();
    const focus = this.focusKey();
    const card = layout?.cards.find(c => c.key === focus) ?? null;

    const arrows: Record<string, Direction> = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
    };
    if (arrows[event.key]) {
      event.preventDefault();
      if (layout && focus) {
        const next = neighbor(layout, focus, arrows[event.key]);
        if (next) this.setFocus(next);
      }
      return;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (card) this.activate(card, event.metaKey || event.ctrlKey);
        break;
      case ' ':
        event.preventDefault();
        if (card?.node) void this.setAnchor({ kind: card.node.kind, id: card.node.id });
        break;
      case 'Backspace':
        event.preventDefault();
        this.back();
        break;
      case 't':
      case 'T':
        event.preventDefault();
        this.toggleView();
        break;
      case '/':
        event.preventDefault();
        this.openSearch();
        break;
    }
  }

  /** Entrée sur une carte : ouvrir la fiche, déplier un repli, recentrer sur un propriétaire externe. */
  activate(card: LayoutCard, newTab: boolean): void {
    if (card.fold) {
      this.toggleFold(card.fold.propertyKey);
      return;
    }
    const node = card.node!;
    if (node.external && !newTab) {
      void this.setAnchor({ kind: node.kind, id: node.id });
      return;
    }
    const commands = recordLink(node.kind, node.id);
    if (newTab) {
      window.open(this.router.serializeUrl(this.router.createUrlTree(commands)), '_blank');
      return;
    }
    this.dialogRef.close();
    void this.router.navigate(commands);
  }

  /**
   * Clic sur une carte. Le lien porte un vrai `href` : Cmd/Ctrl+clic ouvre un
   * onglet sans rien faire ici. Un propriétaire externe recentre au lieu
   * d'ouvrir sa fiche — c'est tout le sens de la colonne.
   */
  onCardClick(event: MouseEvent, card: LayoutCard): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (card.node?.external) {
      event.preventDefault();
      void this.setAnchor({ kind: card.node.kind, id: card.node.id });
      return;
    }
    this.dialogRef.close();
  }

  recenter(event: MouseEvent, node: GraphNode): void {
    event.preventDefault();
    event.stopPropagation();
    void this.setAnchor({ kind: node.kind, id: node.id });
  }

  setFocus(key: string | null, center = false): void {
    this.focusKey.set(key);
    if (!key) return;
    // Après le rendu : la carte active doit rester visible quand le graphe défile.
    setTimeout(() => {
      this.canvas()?.nativeElement
        .querySelector(`#${this.cardId(key)}`)
        ?.scrollIntoView({ block: center ? 'center' : 'nearest', inline: 'nearest' });
    });
  }

  private focusCanvas(): void {
    setTimeout(() => this.canvas()?.nativeElement.focus({ preventScroll: true }));
  }

  /** Clic sur « Recherche » au pied de la palette : ce que fait Cmd+K. */
  onSwitch(tab: PaletteTab): void {
    if (tab !== 'search') return;
    const result: PaletteSwitch = { switchTo: 'search' };
    this.dialogRef.close(result);
  }

  // --- Recherche ---

  openSearch(): void {
    this.searching.set(true);
    setTimeout(() => this.searchInput()?.nativeElement.focus());
  }

  closeSearch(): void {
    if (!this.graph()) return; // hors fiche, la recherche est tout l'écran
    this.searching.set(false);
    this.focusCanvas();
  }

  onSearchInput(value: string): void {
    this.query.set(value);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (value.trim().length < GLOBAL_SEARCH_MIN_LENGTH) {
      this.searchSeq++;
      this.hits.set([]);
      this.searchLoading.set(false);
      return;
    }
    this.searchLoading.set(true);
    this.debounceTimer = setTimeout(async () => {
      const seq = ++this.searchSeq;
      try {
        const hits = await this.searchService.search(value);
        if (seq !== this.searchSeq) return;
        this.hits.set(hits);
      } catch {
        if (seq !== this.searchSeq) return;
        this.hits.set([]);
      }
      this.searchActive.set(0);
      this.searchLoading.set(false);
    }, DEBOUNCE_MS);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const count = this.hits().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) this.searchActive.set((this.searchActive() + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) this.searchActive.set((this.searchActive() - 1 + count) % count);
        break;
      case 'Enter': {
        event.preventDefault();
        const hit = this.hits()[this.searchActive()];
        if (hit) this.pickHit(hit);
        break;
      }
      case 'Escape':
        // Avec un graphe derrière, Échap referme la recherche, pas la palette.
        if (this.graph()) {
          event.preventDefault();
          event.stopPropagation();
          this.closeSearch();
        }
        break;
    }
  }

  pickHit(hit: GlobalSearchHit): void {
    void this.setAnchor({ kind: hit.kind, id: hit.id });
  }

  // --- Affichage ---

  icon(kind: GraphKind): IconDefinition {
    return KIND_ICON[kind];
  }

  kindName(kind: GraphKind): string {
    return KIND_NAME[kind];
  }

  /** Le libellé d'une carte : la période pour un bail — son titre répète ce que disent les cartes voisines. */
  cardLabel(node: GraphNode): string {
    if (node.kind !== 'lease') return node.label;
    const start = monthYear(node.startDate);
    // Les fins « 5000-… » de la reprise veulent dire « sans terme ».
    const end = node.endDate && Number(node.endDate.slice(0, 4)) >= 2200 ? 'sans terme' : monthYear(node.endDate);
    if (!start && !end) return node.label;
    return `${start ?? '?'} → ${end ?? 'sans terme'}`;
  }

  cardSublabel(node: GraphNode): string | null {
    if (node.kind === 'lease') return node.ended ? 'Terminé' : 'En cours';
    if (node.external) return 'Recentrer sur ce propriétaire';
    return node.sublabel;
  }

  cardId(key: string): string {
    return 'graph-card-' + key.replace(/[^a-zA-Z0-9-]/g, '-');
  }

  link(node: GraphNode): string[] {
    return recordLink(node.kind, node.id);
  }
}
